// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { ApiUser } from '../../types/accounts';

interface GroupsTabProps {
  account: ApiUser;
}

interface GroupMembership {
  group: {
    id: string;
    creator_id: string;
    name: string;
    description: string;
    avatar_url: string;
    lang_tag: string;
    metadata: string;
    open: boolean;
    edge_count: number;
    max_count: number;
    create_time: string;
    update_time: string;
  };
  state: number; // 0 = superadmin, 1 = admin, 2 = member, 3 = join request
}

const GroupsTab: React.FC<GroupsTabProps> = ({ account }) => {
  const [groups, setGroups] = useState<GroupMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadGroups();
  }, [account.id]);

  const loadGroups = async () => {
    setLoading(true);
    setError('');

    try {
      // This would need to be implemented in the console service
      // const groupsList = await consoleService.listUserGroups('', account.id);
      // setGroups(groupsList.user_group_list || []);
      
      // For now, simulate loading
      setTimeout(() => {
        setGroups([]);
        setLoading(false);
      }, 1000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load groups');
      setLoading(false);
    }
  };

  const getStateLabel = (state: number) => {
    switch (state) {
      case 0: return 'Superadmin';
      case 1: return 'Admin';
      case 2: return 'Member';
      case 3: return 'Join Request';
      default: return 'Unknown';
    }
  };

  const getStateClass = (state: number) => {
    switch (state) {
      case 0: return 'badge-danger';
      case 1: return 'badge-warning';
      case 2: return 'badge-success';
      case 3: return 'badge-info';
      default: return 'badge-secondary';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="groups-tab">
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <strong>Loading groups...</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="groups-tab">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>Group Memberships</h5>
        <span className="text-muted">{groups.length} groups found</span>
      </div>

      {error && (
        <div className="alert alert-danger">
          <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="me-2" />
          <strong>Error loading groups:</strong> {error}
        </div>
      )}

      {groups.length === 0 && !error ? (
        <div className="alert alert-info">
          <strong>No group memberships found.</strong>
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Group ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Role</th>
                <th>Members</th>
                <th>Open</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((membership, index) => (
                <tr key={membership.group.id}>
                  <td>
                    <code>{membership.group.id}</code>
                  </td>
                  <td>
                    <div className="d-flex align-items-center">
                      {membership.group.avatar_url && (
                        <img
                          src={membership.group.avatar_url}
                          alt=""
                          className="rounded mr-2"
                          width="24"
                          height="24"
                        />
                      )}
                      {membership.group.name}
                    </div>
                  </td>
                  <td>
                    <span className="text-truncate" style={{ maxWidth: '200px' }}>
                      {membership.group.description}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStateClass(membership.state)}`}>
                      {getStateLabel(membership.state)}
                    </span>
                  </td>
                  <td>
                    {membership.group.edge_count}
                    {membership.group.max_count > 0 && (
                      <span className="text-muted"> / {membership.group.max_count}</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${membership.group.open ? 'badge-success' : 'badge-secondary'}`}>
                      {membership.group.open ? 'Open' : 'Closed'}
                    </span>
                  </td>
                  <td>{formatDate(membership.group.create_time)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GroupsTab;
